import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronDown, FiSearch } from 'react-icons/fi';

/**
 * A dark-theme-consistent dropdown for the Add Opportunity / Add Quote forms.
 *
 * Native <select> option lists render with the OS/browser's own (white)
 * styling in most browsers — no amount of CSS on the closed control fixes
 * that. The rest of the app already solves this the same way Momentum
 * Command's "Assign" menu does: a plain button that opens a custom-styled
 * list instead of a native <select>. This is that pattern, generalized for
 * a full-width form field.
 */

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  disabled?: boolean;
  /** Adds a filter input at the top of the open menu — for fields like Line of
   *  business/Stage whose option lists are agency-configured and can run long. */
  searchable?: boolean;
  /** Opens the menu as soon as this mounts — for a click-to-edit field (e.g. a
   *  card's stage badge) where a fresh instance is mounted on click and should
   *  show its list immediately instead of needing a second click to open it. */
  defaultOpen?: boolean;
  /** Renders the open menu into document.body instead of inline — needed when
   *  this sits inside an `overflow: hidden` ancestor (a card, a table row) that
   *  would otherwise clip the menu at its own edge. */
  portal?: boolean;
  /** When set, the search box reports its query here instead of (or in addition
   *  to) filtering `options` locally — for a field like Line of business whose
   *  options come from a server-side search rather than one fixed local list.
   *  The caller is expected to re-fetch and pass back an already-filtered
   *  `options`, so the local substring filter is skipped while this is set. */
  onSearchChange?: (query: string) => void;
}

const AqSelect: React.FC<Props> = ({
  value, onChange, options, placeholder = 'Select…', disabled, searchable, defaultOpen, portal, onSearchChange,
}) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const [query, setQuery] = useState('');
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  // Portaled menu uses position: fixed off the button's own viewport rect —
  // scrolling the page/list would leave it visually detached from the button
  // it belongs to, so a scroll just closes it instead of chasing a reposition.
  useEffect(() => {
    if (!open || !portal) return;
    const updatePos = () => {
      const rect = btnRef.current?.getBoundingClientRect();
      if (rect) setMenuPos({ top: rect.bottom + 6, left: rect.left, width: rect.width });
    };
    updatePos();
    // Capture-phase scroll fires for the menu's own scrollable option list too —
    // only treat it as "the page moved" (and close) when the scroll didn't
    // originate inside the menu itself.
    const handleScroll = (e: Event) => {
      if (menuRef.current && e.target instanceof Node && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open, portal]);

  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
    if (!open) setQuery('');
  }, [open, searchable]);

  const filteredOptions = useMemo(() => {
    if (!searchable || onSearchChange || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query, searchable, onSearchChange]);

  const menu = open && (
    <div
      ref={menuRef}
      className="aq-select__menu"
      role="listbox"
      style={portal && menuPos ? { position: 'fixed', top: menuPos.top, left: menuPos.left, width: menuPos.width, right: 'auto', zIndex: 1000 } : undefined}
    >
      {searchable && (
        <div className="aq-select__search">
          <FiSearch size={12} aria-hidden="true" />
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); onSearchChange?.(e.target.value); }}
            placeholder="Search…"
            aria-label="Search options"
          />
        </div>
      )}
      <ul className="aq-select__list">
        {filteredOptions.length === 0 ? (
          onSearchChange ? (
            <li className="aq-select__empty"><span className="aq-select__spinner" aria-hidden="true" /></li>
          ) : (
            <li className="aq-select__empty">No matches</li>
          )
        ) : (
          filteredOptions.map((o, i) => (
            <li key={`${i}-${o}`}>
              <button
                type="button"
                className={`aq-select__item${o === value ? ' aq-select__item--active' : ''}`}
                role="option"
                aria-selected={o === value}
                onClick={() => { onChange(o); setOpen(false); }}
              >
                {o}
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );

  return (
    <div className={`aq-select${portal ? ' aq-select--portal' : ''}`} ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        className={`aq-select__btn${!value ? ' aq-select__btn--placeholder' : ''}`}
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{value || placeholder}</span>
        <FiChevronDown size={13} className="aq-select__caret" />
      </button>
      {portal ? (menu && createPortal(menu, document.body)) : menu}
    </div>
  );
};

export default AqSelect;
